import { PageHeader } from "../../../CommonElements/PageTitle";
import { Container, Row, Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import { TableC } from "../../../CommonElements/TableC";
import { Divider, Tabs } from "antd";
import { useHousingDetailsQuery } from "../../../services/housing";
import { formatDate } from "../../../utils/helpers";
import { SelectCountry } from "../../../Component/Form/SelectCountry/SelectCountry";
import { LabelValue } from "@/Component/Value";
import { Link } from "react-router";

const VLabelValue = ({ ...props }) => (
  <Col xs={6} sm={6} md={4} lg={3} className="mb-3">
    <LabelValue {...props} value={props.value ?? "-"} direction="vertical" />
  </Col>
);

export const HousingDetails = () => {
  const { t, i18n } = useTranslation();
  const { data: housingDetails, isLoading: isHousingDetailsLoading } =
    useHousingDetailsQuery();
  const housingDetailsData = housingDetails?.data?.data || [];

  const copyValue = `${housingDetailsData?.house?.Latitude}, ${housingDetailsData?.house?.Longitude}`;
  const hasLongitudeAndLatitude = !!(
    housingDetailsData?.house?.Latitude && housingDetailsData?.house?.Longitude
  );

  const source_type =
    housingDetailsData?.contracts?.[0]?.source_type?.toUpperCase() ||
    "CAMPAIGN";

  const columns = [
    {
      name: t(`${source_type}_NUMBER`),
      key: "campaign_number",
    },
    {
      name: t(`${source_type}_NAME`),
      key: "campaign_name",
    },
    {
      name: t("HOUSING_DETAILS_COL_COUNTRY"),
      key: "country_id",
      render: (value) => <SelectCountry.Display value={value} />,
    },
    {
      name: t("HOUSING_DETAILS_COL_PROVINCE_CITY"),
      key: "province_name",
    },
    {
      name: t("HOUSING_DETAILS_COL_SERVICE_CENTER"),
      key: "service_center_name",
    },
    {
      name: t("HOUSING_DETAILS_COL_CAPACITY"),
      key: "capacity",
    },
    {
      name: t("HOUSING_CONTRACT"),
      key: "contract_id",
      render: (value) => (
        <Link to={`/app/makkah/housing-contracts/${value}/view`}>
          <LabelValue.Value value={value} />
        </Link>
      ),
    },
    {
      name: t("HOUSING_DETAILS_COL_START_DATE"),
      key: "contractStartDate",
      render: (value) => formatDate(value, i18n.language, "DD-MM-YYYY"),
    },
    {
      name: t("HOUSING_DETAILS_COL_END_DATE"),
      key: "contractEndDate",
      render: (value) => formatDate(value, i18n.language, "DD-MM-YYYY"),
    },
    {
      name: t("ACTUAL_PILGRIMS_HOUSED"),
      key: "numberOfAccommodatedPilgrims",
      render: (value) => value || "-",
    },
    {
      name: t("ACTUAL_PILGRIMS_DEPARTED"),
      key: "departedPilgrims",
    },
    {
      name: t("ACTUAL_PILGRIMS_REMAINING"),
      key: "remainingPilgrims",
      render: ($, rowData) => {
        const remaining = +rowData?.numberOfAccommodatedPilgrims - +rowData?.departedPilgrims;

        return <LabelValue.Value value={remaining <= 0 ? "-" : remaining || "-"} />;
      },
      visible: false,
    },
  ];

  const sketchImageUrl = housingDetailsData?.house?.SketchImageUrl;

  return (
    <div>
      <PageHeader title={t("HOUSING_DETAILS")} />
      <Container fluid>
        <Row>
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("BUILDING_NO")}
            value={housingDetailsData?.house?.BuildingNo}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("AREA_NO")}
            value={housingDetailsData?.house?.AreaNo}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("BUILDING_NAME")}
            value={housingDetailsData?.house?.BuildingName}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("BUILDING_CITY")}
            value={housingDetailsData?.house?.houseCityName}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("BUILDING_ADDRESS")}
            value={housingDetailsData?.house?.HOUSE_ADDRESS_1}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("HOUSING_LOCATION_URL")}
            value={
              housingDetailsData?.house?.Location &&
              /^https?:\/\//i.test(String(housingDetailsData.house.Location).trim()) ? (
                <a
                  href={String(housingDetailsData.house.Location).trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {housingDetailsData.house.Location}
                </a>
              ) : (
                housingDetailsData?.house?.Location
              )
            }
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("DELEGATE_NAME")}
            value={housingDetailsData?.house?.OwnerName}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("MOBILE_NUMBER")}
            value={housingDetailsData?.house?.REP_MOBILE_NO}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("CONTRACTS_COUNT")}
            value={housingDetailsData?.contracts?.length || 0}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("COORDINATES")}
            copyValue={hasLongitudeAndLatitude ? copyValue : undefined}
            copyOnValueClick
            value={
              hasLongitudeAndLatitude
                ? `${housingDetailsData?.house?.Latitude}, ${housingDetailsData?.house?.Longitude}`
                : "-"
            }
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("BUILDING_CODE")}
            value={housingDetailsData?.house?.id}
          />
          <VLabelValue
            loading={isHousingDetailsLoading}
            label={t("PERMIT_NO")}
            value={housingDetailsData?.house?.houseComrRegNo}
          />

        </Row>

        <Divider />

        <Tabs
          items={[
            {
              key: "contracts",
              label: t("HOUSING_DETAILS_CONTRACT_DATA_TAB"),
              children: (
                <TableC
                  columns={columns}
                  data={housingDetailsData?.contracts || []}
                  loading={isHousingDetailsLoading}
                />
              ),
              disabled: isHousingDetailsLoading,
            },
            {
              disabled: isHousingDetailsLoading,
              key: "sketch",
              label: t("HOUSING_DETAILS_SKETCH_TAB"),
              children: (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  {!!sketchImageUrl ? (
                    <img
                      src={sketchImageUrl}
                      alt={t("HOUSING_DETAILS_SKETCH_TAB")}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "600px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                  ) : (
                    <div style={{ padding: "40px", color: "#999" }}>
                      {t("NO_DATA")}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Container>
    </div>
  );
};
